import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { getDeviceConfigDiff, getDeviceSchema, rebuildModelSchema } from '../../api/apiClient';

const ConfigValidatePage = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [schema, setSchema] = useState(null);
  const [parsedConfig, setParsedConfig] = useState(null);
  const [showNormalizedPreview, setShowNormalizedPreview] = useState(false);
  const [rebuildingSchema, setRebuildingSchema] = useState(false);

  const loadDiff = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getDeviceConfigDiff(deviceId);
      setDiffData(response);
    } catch (err) {
      console.error('Error loading diff:', err);
      setError(err.message || 'Failed to load validation data');
    } finally {
      setLoading(false);
    }
  };

  const loadSchema = async () => {
    if (!diffData?.model) return;
    
    try {
      const response = await getDeviceSchema(diffData.model);
      setSchema(response);
      setSchemaDialogOpen(true);
    } catch (err) {
      console.error('Error loading schema:', err);
      setError(err.message || 'Failed to load schema');
    }
  };

  const loadParsedConfig = async () => {
    try {
      if (diffData?.parsedConfig) {
        setParsedConfig(diffData.parsedConfig);
        setConfigDialogOpen(true);
      } else {
        setParsedConfig(null);
        setError('No parsed config available for this device yet.');
      }
    } catch (err) {
      console.error('Error loading parsed config:', err);
      setError(err.message || 'Failed to load parsed config');
    }
  };

  useEffect(() => {
    if (deviceId) {
      loadDiff();
    }
  }, [deviceId]);

  const handleRebuildSchema = async () => {
    if (!diffData?.model) return;
    setRebuildingSchema(true);
    setError(null);
    try {
      await rebuildModelSchema(diffData.model);
      await loadDiff();
    } catch (err) {
      console.error('Error rebuilding schema:', err);
      setError(err.message || 'Failed to rebuild schema');
    } finally {
      setRebuildingSchema(false);
    }
  };

  const getStatusChip = (status) => {
    const configs = {
      ok: { label: 'OK', color: 'success', icon: <CheckCircleIcon /> },
      missing: { label: 'Missing', color: 'error', icon: <ErrorIcon /> },
      missing_optional: { label: 'Missing (Optional)', color: 'warning', icon: <WarningIcon /> },
      extra: { label: 'Extra', color: 'info', icon: <AddIcon /> },
      type_mismatch: { label: 'Type Mismatch', color: 'error', icon: <ErrorIcon /> }
    };
    
    const config = configs[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
  };

  const columns = [
    {
      field: 'field',
      headerName: 'Field',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'schemaType',
      headerName: 'Expected Type',
      width: 130,
      renderCell: (params) => params.value || '—'
    },
    {
      field: 'actualType',
      headerName: 'Actual Type',
      width: 130,
      renderCell: (params) => params.value || '—'
    },
    {
      field: 'value',
      headerName: 'Actual Value',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        if (params.value === null || params.value === undefined) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const valueStr = typeof params.value === 'object' ? JSON.stringify(params.value) : String(params.value);
        return (
          <Typography variant="body2" sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {valueStr}
          </Typography>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'required',
      headerName: 'Required',
      width: 100,
      renderCell: (params) => params.value ? 'Yes' : 'No'
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const isNoParsedConfig = error.includes('No parsed config found');
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={isNoParsedConfig ? 'warning' : 'error'} sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {error}
          </Typography>
          {isNoParsedConfig && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              This device needs a config snapshot to be downloaded and parsed first.
              <br />
              <strong>Steps to fix:</strong>
              <br />
              1. Go back to Devices page
              <br />
              2. Click the Download icon (↓) to pull the device config via agent
              <br />
              3. Wait for the config to be processed and parsed
              <br />
              4. Return here to validate
            </Typography>
          )}
        </Alert>
        <Stack direction="row" spacing={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/event-system/devices')}>
            Back to Devices
          </Button>
          {isNoParsedConfig && (
            <Button 
              variant="contained" 
              onClick={() => navigate('/admin/config-snapshots')}
            >
              View Config Snapshots
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  if (!diffData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No validation data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/admin/event-system/devices')}
          sx={{ mb: 2 }}
        >
          Back to Devices
        </Button>
        
        <Typography variant="h4" gutterBottom>
          Config Validation
        </Typography>
        
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Chip label={`Device: ${diffData.deviceId}`} />
          <Chip label={`Model: ${diffData.model}`} />
          <Chip label={`Schema: ${diffData.schemaVersion}`} />
          <Chip 
            label={diffData.valid ? 'Valid' : 'Invalid'} 
            color={diffData.valid ? 'success' : 'error'}
            icon={diffData.valid ? <CheckCircleIcon /> : <ErrorIcon />}
          />
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Chip 
            label={`✅ OK: ${diffData.summary.ok}`} 
            color="success" 
            variant="outlined"
          />
          <Chip 
            label={`⚠️ Missing: ${diffData.summary.missing}`} 
            color="error" 
            variant="outlined"
          />
          <Chip 
            label={`⚠️ Missing (Optional): ${diffData.summary.missing_optional || 0}`} 
            color="warning" 
            variant="outlined"
          />
          <Chip 
            label={`➕ Extra: ${diffData.summary.extra}`} 
            color="info" 
            variant="outlined"
          />
          <Chip 
            label={`🧩 Type Mismatch: ${diffData.summary.type_mismatch}`} 
            color="error" 
            variant="outlined"
          />
        </Stack>
      </Paper>

      {/* Actions */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button 
          variant="outlined" 
          startIcon={<VisibilityIcon />}
          onClick={loadSchema}
        >
          View Normalized Schema
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<VisibilityIcon />}
          onClick={loadParsedConfig}
        >
          View Parsed Config
        </Button>
        <Button 
          variant="outlined" 
          startIcon={showNormalizedPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setShowNormalizedPreview(!showNormalizedPreview)}
        >
          {showNormalizedPreview ? 'Hide' : 'Show'} Normalized Preview
        </Button>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={loadDiff}
        >
          Refresh Validation
        </Button>
        <Button
          variant="outlined"
          startIcon={rebuildingSchema ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={handleRebuildSchema}
          disabled={rebuildingSchema || !diffData?.model}
        >
          {rebuildingSchema ? 'Rebuilding Schema...' : 'Rebuild Schema'}
        </Button>
      </Stack>

      {/* Normalized Preview */}
      <Collapse in={showNormalizedPreview}>
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Normalized Preview (Valid Fields Only)
          </Typography>
          <Box 
            component="pre" 
            sx={{ 
              overflow: 'auto', 
              maxHeight: '300px',
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}
          >
            {JSON.stringify(diffData.normalizedPreview, null, 2)}
          </Box>
        </Paper>
      </Collapse>

      {/* Validation Details Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={diffData.details.map((detail, idx) => ({ id: idx, ...detail }))}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          density="comfortable"
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Paper>

      {/* Schema Dialog */}
      <Dialog 
        open={schemaDialogOpen} 
        onClose={() => setSchemaDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Normalized Schema: {diffData.model}
        </DialogTitle>
        <DialogContent>
          {schema && (
            <Box 
              component="pre" 
              sx={{ 
                overflow: 'auto',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 1
              }}
            >
              {JSON.stringify(schema, null, 2)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Parsed Config Dialog */}
      <Dialog 
        open={configDialogOpen} 
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Parsed Config: {diffData.deviceId}
        </DialogTitle>
        <DialogContent>
          {parsedConfig && (
            <Box 
              component="pre" 
              sx={{ 
                overflow: 'auto',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 1
              }}
            >
              {JSON.stringify(parsedConfig, null, 2)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigValidatePage;
